import { useTranslation } from 'react-i18next';

export const EmployeeCountStep = ({ formData, onChange, onNext, onPrevious }) => {
    const { t } = useTranslation();
    const employeeOptions = [
        { range: '0-10', icon: 'user' },
        { range: '10-30', icon: 'users', selected: true },
        { range: '30-50', icon: 'users-more' },
        { range: '50-100', icon: 'building' },
        { range: '100+', icon: 'chart-bar' }
    ];

    const managementOptions = [
        {
            id: 'no-payslip',
            title: t('employeeCountSetup.noPayslipYet'),
            icon: 'calendar',
            selected: true
        },
        {
            id: 'accountant',
            title: t('employeeCountSetup.hasAccountant'),
            icon: 'calculator'
        },
        {
            id: 'software',
            title: t('employeeCountSetup.usesSoftware'),
            icon: 'computer'
        }
    ];

    return (
        <div className="w-full">
            <h2 className="text-2xl font-bold mb-4">{t('employeeCountSetup.howManyEmployees')}</h2>
            <p className="mb-6 text-gray-600">{t('employeeCountSetup.startupHint')}</p>

            <div className="flex justify-between mb-12">
                {employeeOptions.map((option, index) => (
                    <div
                        key={index}
                        className={`flex flex-col items-center p-6 rounded-lg border cursor-pointer ${formData.employeeCount === option.range ? 'border-[--color-primary] bg-blue-50' : 'border-gray-200'
                            }`}
                        onClick={() => onChange('employeeCount', option.range)}
                    >
                        <div className={`w-12 h-12 flex items-center justify-center rounded-full ${formData.employeeCount === option.range ? 'text-[--color-primary]' : 'text-gray-500'
                            }`}>
                            {/* Placeholder for icons */}
                            <svg className="w-6 h-6" viewBox="0 0 24 24">
                                {option.range === '0-10' && <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />}
                                {option.range === '10-30' && <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />}
                                {option.range === '30-50' && <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />}
                                {option.range === '50-100' && <path fill="currentColor" d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />}
                                {option.range === '100+' && <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />}
                            </svg>
                        </div>
                        <span className="mt-2 text-sm font-medium">{option.range}</span>
                    </div>
                ))}
            </div>

            <h2 className="text-2xl font-bold mb-4">{t('employeeCountSetup.howManagePayroll')}</h2>
            <p className="mb-6 text-gray-600">{t('companyInfoSetup.subtitle')}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                {managementOptions.map((option) => (
                    <div
                        key={option.id}
                        className={`p-6 border rounded-lg flex flex-col items-center text-center cursor-pointer ${formData.payrollManagement === option.id ? 'border-[--color-primary] bg-blue-50' : 'border-gray-200'
                            }`}
                        onClick={() => onChange('payrollManagement', option.id)}
                    >
                        <div className={`w-12 h-12 mb-4 flex items-center justify-center ${formData.payrollManagement === option.id ? 'text-[--color-primary]' : 'text-gray-500'
                            }`}>
                            {/* Placeholder for icons */}
                            <svg className="w-6 h-6" viewBox="0 0 24 24">
                                {option.id === 'no-payslip' && <path fill="currentColor" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />}
                                {option.id === 'accountant' && <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 14H8c-.55 0-1-.45-1-1s.45-1 1-1h8c.55 0 1 .45 1 1s-.45 1-1 1zm0-4H8c-.55 0-1-.45-1-1s.45-1 1-1h8c.55 0 1 .45 1 1s-.45 1-1 1zm0-4H8c-.55 0-1-.45-1-1s.45-1 1-1h8c.55 0 1 .45 1 1s-.45 1-1 1z" />}
                                {option.id === 'software' && <path fill="currentColor" d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />}
                            </svg>
                        </div>
                        <p className="text-sm font-medium">{option.title}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};